import { FC, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import buildingProgramFemaleImg from 'url:../../ProgramGeneratorAI/assets/client/building-program-female.png';
import buildingProgramMaleImg from 'url:../../ProgramGeneratorAI/assets/client/building-program-male.png';

import { ReactComponent as OverviewOverviewEquipmentIcon } from 'src/assets/images/trainer/clients/overview-overview-equipment.svg';
import { ReactComponent as OverviewOverviewFrequencyIcon } from 'src/assets/images/trainer/clients/overview-overview-frequency.svg';
import { ReactComponent as OverviewOverviewApproachIcon } from 'src/assets/images/trainer/clients/overview-overview-approach.svg';
import { ReactComponent as OverviewOverviewDurationIcon } from 'src/assets/images/trainer/clients/overview-overview-duration.svg';
import { ReactComponent as OverviewOverviewWeekIcon } from 'src/assets/images/trainer/clients/overview-overview-week.svg';
import { ReactComponent as OverviewOverviewThisWeekIcon } from 'src/assets/images/trainer/clients/overview-overview-this_week.svg';
import { ReactComponent as OverviewOverviewWorkoutsCompletedIcon } from 'src/assets/images/trainer/clients/overview-overview-workouts_completed.svg';
import { ReactComponent as OverviewOverviewTrainingVolumeIcon } from 'src/assets/images/trainer/clients/overview-overview-training_volume.svg';
import { ReactComponent as OverviewOverviewCompletionRateIcon } from 'src/assets/images/trainer/clients/overview-overview-completion_rate.svg';
import { ReactComponent as OverviewOverviewRecentActivityIcon } from 'src/assets/images/trainer/clients/overview-overview-recent_activity.svg';
import { ReactComponent as OverviewOverviewMyNotesIcon } from 'src/assets/images/trainer/clients/overview-overview-my_notes.svg';
import { ReactComponent as OverviewOverviewViewAllArrowIcon } from 'src/assets/images/trainer/clients/overview-overview-view_all_arrow.svg';
import { ReactComponent as OverviewOverviewAddNoteIcon } from 'src/assets/images/trainer/clients/overview-overview-add_note.svg';
import { ReactComponent as OverviewOverviewAiObservationsIcon } from 'src/assets/images/trainer/clients/overview-overview-ai_observations.svg';
import { ReactComponent as OverviewOverviewProgramOverviewIcon } from 'src/assets/images/trainer/clients/overview-overview-program-overview.svg';
import { ReactComponent as OverviewOverviewSessionTimeIcon } from 'src/assets/images/trainer/clients/overview-overview-session-time.svg';
import { ReactComponent as OverviewOverviewViewProgramIcon } from 'src/assets/images/trainer/clients/overview-overview-view-program.svg';

import { AddNoteModal } from './AddNoteModal/AddNoteModal';
import { useGetClientOverview } from 'src/api/queries/useGetClientOverview';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { isFemaleProfile } from 'src/utils/genderUtils';

import { EmptyState } from './EmptyState';

interface OverviewTabProps {
  client?: any;
}

export const OverviewTab: FC<OverviewTabProps> = () => {
  const { uid } = useParams();
  const { client } = useOutletContext<{ client: any }>();
  const clientName = client ? `${client.name || ''} ${client.surname || ''}`.trim() : undefined;
  const navigate = useNavigate();
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);

  const { data: overviewData, isLoading } = useGetClientOverview(uid || '');
  const { data: masterData } = useGetProgramMasterData();

  const getSlugTitle = (slug: string): string => {
    if (!slug) return '';
    let result: string | null = null;
    if (masterData) {
      const search = (obj: any) => {
        if (result) return;
        if (!obj || typeof obj !== 'object') return;
        if ((obj.slug === slug || obj.id === slug) && obj.title) {
          result = obj.title;
          return;
        }
        if (Array.isArray(obj)) {
          obj.forEach(search);
        } else {
          Object.values(obj).forEach(search);
        }
      };
      search(masterData);
    }

    if (result) return result;

    return String(slug)
      .replace(/_/g, '-')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatEquipment = (equip: string[]) => {
    if (!equip || equip.length === 0) return 'None';
    const formatted = equip.map((e) =>
      e
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
    );
    if (formatted.length === 1) return formatted[0];
    return `${formatted[0]}, +${formatted.length - 1} other`;
  };

  const formatDate = (isoStr: string | null | undefined) => {
    if (!isoStr) return '--';
    if (isoStr.includes('•')) return isoStr;
    try {
      const parsed = dayjs(isoStr);
      if (parsed.isValid()) {
        const currentYear = dayjs().year();
        const finalDate =
          parsed.year() === 2001 && !isoStr.includes('2001') ? parsed.year(currentYear) : parsed;
        return finalDate.format('MMM DD, YYYY • h:mm A');
      }
      return isoStr;
    } catch {
      return isoStr;
    }
  };

  if (isLoading) {
    return (
      <div className="overview-content loading">
        <Spin indicator={<LoadingOutlined className="loading-icon" spin />} />
        <span className="loading-text">Loading overview</span>
      </div>
    );
  }

  const overview = overviewData?.overview;
  const thisWeek = overviewData?.thisWeek;
  const recentActivity = overviewData?.recentActivity;
  const aiObservations = overviewData?.aiObservations || [];
  const myNotes = overviewData?.myNotes || [];

  const isDraftProgram = overview?.isDraft === true || overview?.status === 'draft';
  const generatorType =
    overview?.generatorType === 'manual' || (overview as any)?.type === 'manual' ? 'manual' : 'ai';
  const isInactive = client?.is_active === false || client?.isActive === false;

  if (!overview?.programId || isDraftProgram) {
    return (
      <div className="overview-content">
        <div
          className="dashboard-empty-state-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%',
          }}
        >
          <EmptyState
            message={
              isInactive
                ? 'This client is currently inactive. Activate this client to create and assign programs.'
                : isDraftProgram
                  ? 'You have an unfinished program draft for this client.'
                  : 'No program yet.'
            }
            buttonText={
              isInactive
                ? undefined
                : isDraftProgram
                  ? 'Continue to Finish Program'
                  : 'Create Your First Program'
            }
            onButtonClick={
              isInactive
                ? undefined
                : isDraftProgram
                  ? () =>
                      navigate(
                        `/onboarding-trainer/dashboard/program-generator/${generatorType}?programId=${overview.programId}`,
                      )
                  : () => navigate('/onboarding-trainer/dashboard')
            }
            hideIcon
            hideMessage={false}
            marginTop="0"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="overview-content">
      <div className="overview-grid">
        <div className="program-overview-card">
          <div className="card-content">
            <div className="card-header">
              <div className="title-group">
                <OverviewOverviewProgramOverviewIcon />
                <h3>Program Overview</h3>
              </div>
              <span className="active-badge">
                <div className="dot"></div> ACTIVE
              </span>
            </div>

            <div className="program-body-split">
              <div className="program-info">
                <div className="program-details">
                  <div className="program-summary">
                    <h4>{overview?.programName || 'Workout Program'}</h4>
                    <p className="goal">
                      Goal:{' '}
                      <span>{getSlugTitle(overview?.primaryGoal || '') || 'Not specified'}</span>
                    </p>
                  </div>

                  <div className="stats-grid grid-1">
                    <div className="stat-item">
                      {<OverviewOverviewApproachIcon />}
                      <div>
                        <span className="label">Approach</span>
                        <span className="value">
                          {getSlugTitle(
                            (overview as any)?.goalApproach || overview?.approach || '',
                          ) || '--'}
                        </span>
                      </div>
                    </div>
                    <div className="stat-item">
                      {<OverviewOverviewFrequencyIcon />}
                      <div>
                        <span className="label">Frequency</span>
                        <span className="value">{overview?.frequency || 0} days / week</span>
                      </div>
                    </div>
                  </div>

                  <div className="stats-grid grid-2">
                    <div className="stat-item">
                      {<OverviewOverviewWeekIcon />}
                      <div>
                        <span className="label">Week</span>
                        <span className="value">
                          {overview?.week
                            ? String(overview.week).toLowerCase().includes('not started')
                              ? overview.week
                              : `Week ${overview.week}`
                            : 'Week 1'}
                        </span>
                      </div>
                    </div>
                    <div className="stat-item">
                      <OverviewOverviewSessionTimeIcon />
                      <div>
                        <span className="label">Session Time</span>
                        <span className="value">{overview?.sessionTime || 0} min</span>
                      </div>
                    </div>
                    <div className="stat-item">
                      {<OverviewOverviewEquipmentIcon />}
                      <div>
                        <span className="label">Equipment</span>
                        <span className="value" style={{ textTransform: 'capitalize' }}>
                          {formatEquipment(overview?.equipment || [])}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-bg">
                  <img
                    src={
                      isFemaleProfile(client) ? buildingProgramFemaleImg : buildingProgramMaleImg
                    }
                    alt="Building Program"
                  />
                </div>
              </div>
              <button
                className={`view-program-btn ${isDraftProgram ? 'continue-program-btn' : ''}`}
                onClick={() => {
                  if (isDraftProgram) {
                    navigate(
                      `/onboarding-trainer/dashboard/program-generator/ai?programId=${overview.programId}`,
                    );
                  } else {
                    navigate(
                      `/onboarding-trainer/dashboard/program-generated/${overview.programId}`,
                      {
                        state: {
                          clientId: uid,
                          clientName,
                          isManually: generatorType === 'manual',
                          generatorType,
                        },
                      },
                    );
                  }
                }}
              >
                <OverviewOverviewViewProgramIcon />{' '}
                {isDraftProgram ? 'Continue Program' : 'View Program'}
              </button>
            </div>
          </div>
        </div>
        <div className="this-week-card">
          <div className="card-header">
            {<OverviewOverviewThisWeekIcon />}
            <h3>This Week</h3>
          </div>
          <div className="progress-stats">
            <div className="progress-item">
              <div className="icon-container">{<OverviewOverviewWorkoutsCompletedIcon />}</div>
              <div className="progress-content">
                <div className="progress-labels">
                  <span className="label">Workouts completed</span>
                  <span className="value">
                    {thisWeek?.workoutsCompleted || 0} / {thisWeek?.totalWorkouts || 0}
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${thisWeek?.totalWorkouts ? ((thisWeek?.workoutsCompleted || 0) / thisWeek?.totalWorkouts) * 100 : 0}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="progress-item">
              <div className="icon-container">{<OverviewOverviewDurationIcon />}</div>
              <div className="progress-content">
                <div className="progress-labels">
                  <span className="label">Duration</span>
                  <span className="value">{thisWeek?.duration || 0} min</span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(100, ((thisWeek?.duration || 0) / Math.max(1, (overview?.sessionTime || 45) * (thisWeek?.totalWorkouts || 3))) * 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="progress-item">
              <div className="icon-container">{<OverviewOverviewTrainingVolumeIcon />}</div>
              <div className="progress-content">
                <div className="progress-labels">
                  <span className="label">Training volume</span>
                  <span className="value">{thisWeek?.trainingVolume || '--'}</span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(() => {
                        const parts = (thisWeek?.trainingVolume || '').split('/');
                        const completed = parseFloat((parts[0] || '').replace(/[^0-9.]/g, '')) || 0;
                        const planned = parseFloat((parts[1] || '').replace(/[^0-9.]/g, '')) || 0;
                        return planned > 0
                          ? Math.min(100, Math.max(0, (completed / planned) * 100))
                          : completed > 0
                            ? 100
                            : 0;
                      })()}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="progress-item">
              <div className="icon-container">{<OverviewOverviewCompletionRateIcon />}</div>
              <div className="progress-content">
                <div className="progress-labels">
                  <span className="label">Completion rate</span>
                  <span className="value">{thisWeek?.completionRate || '--'}</span>
                </div>
                <div className="progress-bar-container">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, parseInt(thisWeek?.completionRate || '0', 10) || 0))}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="recent-activity-card">
          <div className="card-header">
            {<OverviewOverviewRecentActivityIcon />}
            <h3>Recent Activity</h3>
          </div>
          <div className="activity-list">
            <div className="activity-item">
              <span className="title">Last workout completed</span>
              <span className="date">{formatDate(recentActivity?.lastCompleted)}</span>
            </div>
            <div className="activity-item">
              <span className="title">Last check-in</span>
              <span className="date">{formatDate(recentActivity?.lastCheckin)}</span>
            </div>
            <div className="activity-item">
              <span className="title">Next scheduled workout</span>
              <span className="date">
                {recentActivity?.nextScheduled ? formatDate(recentActivity.nextScheduled) : '--'}
              </span>
            </div>
          </div>
        </div>
        <div className="notes-card">
          <div className="card-header">
            <div className="title-group">
              {<OverviewOverviewMyNotesIcon />}
              <h3>My Notes</h3>
            </div>
            <button
              className="view-all-btn"
              onClick={() =>
                navigate(`/onboarding-trainer/dashboard/clients/${uid}/overview/notes`)
              }
            >
              View All {<OverviewOverviewViewAllArrowIcon />}
            </button>
          </div>
          <div className="notes-list">
            {myNotes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon-container">
                  <OverviewOverviewMyNotesIcon />
                </div>
                <p>No notes yet.</p>
              </div>
            ) : (
              myNotes.map((note) => (
                <div key={note.id} className="note-item">
                  <div className="note-content">
                    <span className="dot"></span>
                    <p>{note.title}</p>
                  </div>
                  <span className="date">{formatDate(note.date)}</span>
                </div>
              ))
            )}
          </div>
          <button className="add-note-btn" onClick={() => setIsAddNoteModalOpen(true)}>
            {<OverviewOverviewAddNoteIcon />} Add Note
          </button>
        </div>
        <div className="ai-observations-card">
          <div className="card-header">
            {<OverviewOverviewAiObservationsIcon />}
            <h3>AI Observations</h3>
          </div>
          <div className="observations-list">
            {aiObservations.length === 0 ? (
              <p className="empty-state">No observations.</p>
            ) : (
              aiObservations.map((obs) => (
                <div key={obs.id} className="observation-item">
                  <span className="dot"></span>
                  <p>{obs.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
        <AddNoteModal open={isAddNoteModalOpen} onClose={() => setIsAddNoteModalOpen(false)} />
      </div>
    </div>
  );
};
